import { Link } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";
import type { ShopifyProduct } from "@/lib/shopify";
import { Button } from "@/components/ui/button";
import { SOLD_OUT, PREORDER_ACTIVE, PREORDER_NOTE_SHORT, isComingSoon } from "@/lib/storeStatus";
import { PriceBlock } from "@/components/PriceBlock";
import { useInViewOnce } from "@/hooks/useInViewOnce";
import { getProductHeroImage } from "@/lib/productHeroImages";
import { UrgencyLabel } from "@/components/urgency/UrgencyLabel";
import { useCartStore } from "@/stores/cartStore";

function getOverrideImage(title: string, handle: string): string | null {
  return getProductHeroImage(title, handle);
}

export function ProductCard({ product, onQuickAdd }: { product: ShopifyProduct["node"]; onQuickAdd?: () => void }) {
  const img = product.images.edges[0]?.node;
  const overrideSrc = getOverrideImage(product.title, product.handle);
  const price = parseFloat(product.priceRange.minVariantPrice.amount);
  const compare = product.compareAtPriceRange?.minVariantPrice?.amount
    ? parseFloat(product.compareAtPriceRange.minVariantPrice.amount)
    : null;

  // Heuristic ingredient highlight from product type / tags
  const tagLower = (product.tags ?? []).map((t) => t.toLowerCase());
  const titleLower = product.title.toLowerCase();
  const highlight =
    titleLower.includes("mag") ? "Superior absorption" :
    titleLower.includes("liver") ? "Gut Liver Axis" :
    titleLower.includes("ova") ? "Hormonal harmony" :
    (tagLower.find((t) => /clinical|bioavail|patented|vegan|usda/.test(t)) ?? "Effective doses");

  const comingSoon = isComingSoon(product.handle);
  const hasDiscount = compare !== null && compare > price;
  const { ref } = useInViewOnce<HTMLDivElement>();
  const addItems = useCartStore((s) => s.addItems);

  // Pre-book adds the default pack and opens the cart drawer immediately.
  async function handlePrebook() {
    const variant = product.variants?.edges?.[0]?.node;
    if (!variant) return;
    await addItems([
      {
        variantId: variant.id,
        productHandle: product.handle,
        productTitle: product.title,
        imageUrl: img?.url ?? null,
        price: variant.price,
        quantity: 1,
      },
    ]);
    (window as unknown as { __openCart?: () => void }).__openCart?.();
  }


  return (
    <div ref={ref} className="group relative flex flex-col overflow-hidden rounded-3xl border border-border/70 bg-card transition-all duration-500 hover:-translate-y-1 hover:border-brand/25 hover:shadow-luxury-lg">
      {/* subtle gradient ring on hover */}
      <div className="pointer-events-none absolute inset-0 rounded-3xl opacity-0 transition-opacity duration-500 group-hover:opacity-100 [background:linear-gradient(135deg,color-mix(in_oklab,var(--brand)_8%,transparent),transparent_50%)]" />

      <Link
        to="/products/$handle"
        params={{ handle: product.handle }}
        className="relative block aspect-square overflow-hidden bg-gradient-to-br from-brand-soft to-white"
      >
        {/* molecular wash */}
        <div className="pointer-events-none absolute inset-0 bg-molecules opacity-30" />
        {(overrideSrc || img) && (
          <img
            src={overrideSrc ?? img!.url}
            alt={img?.altText ?? product.title}
            loading="lazy"
            decoding="async"
            className={`relative h-full w-full transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04] ${
              overrideSrc ? "object-cover" : "object-contain p-4"
            }`}
          />
        )}
        {compare && compare > price && (
          <span className="absolute left-3 top-3 rounded-full bg-brand-deep/95 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white shadow-luxury">
            Save ₹{(compare - price).toFixed(0)}
          </span>
        )}
        <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full glass-chip px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-brand-deep">
          <Sparkles className="h-3 w-3 text-brand" /> {highlight}
        </span>

      </Link>
      <div className="flex flex-1 flex-col gap-3 p-6">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-brand">
            {product.productType}
          </p>
          <Link to="/products/$handle" params={{ handle: product.handle }}>
            <h3 className="mt-1.5 font-display text-lg font-bold leading-snug text-brand-deep transition-colors group-hover:text-brand md:text-xl">
              {product.title}
            </h3>
          </Link>
          <UrgencyLabel handle={product.handle} className="mt-2" />
          {comingSoon ? (
            <p className="mt-2 text-[11px] font-semibold text-brand">Coming soon</p>
          ) : PREORDER_ACTIVE && !SOLD_OUT && (
            <p className="mt-2 text-[11px] font-semibold text-brand">{PREORDER_NOTE_SHORT}</p>
          )}
        </div>
        <div className="mt-auto flex items-end justify-between gap-3 pt-2">
          <div data-testid="price-countdown">
            <PriceBlock price={price} compareAt={compare} days={30} size="md" />
          </div>
          {comingSoon ? (
            <span className="inline-flex items-center rounded-full border border-border bg-muted px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Coming soon
            </span>
          ) : SOLD_OUT ? (
            <span className="inline-flex items-center rounded-full border border-border bg-muted px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Sold out
            </span>
          ) : (
            <Button
              size="sm"
              className="cta-glow rounded-full px-5 font-semibold"
              onClick={onQuickAdd ?? handlePrebook}
            >
              Buy now
            </Button>
          )}

        </div>
      </div>
    </div>
  );
}
